import Decimal from 'decimal.js';
import type { ShowUnit } from './show';
import { rub } from './format';

/**
 * Порог: единственное число, которым человек настраивает ленту.
 *
 * Полей могло быть семь — минимальный ROI, максимальный возраст, глубина,
 * список площадок, комиссия, шаг ставки, потолок цены. Ни одно из них человек
 * не настраивает: он знает единственное — от скольки рублей ему стоит вставать
 * со стула. Остальное выводится из этого числа.
 *
 * Отдельным файлом, а не в компоненте панели: панель переехала в сито (08.08,
 * владелец — «это же чистые фильтры, им место туда»), а арифметика порога нужна
 * и ленте, и клавиатуре, и ярусам. Держать её в файле экрана значило бы, что
 * следующий переезд панели тащит за собой правку импортов половины ленты.
 */

/** Шаг порога. Сотня — цена одного нажатия, ниже начинается бессмысленная точность. */
export const GATE_STEP = 100;

/** Пол «беру от»: как у детектора, −5 % банка. Ниже — уже не вилка, а дыра. */
export const gateFloor = (bank: number): number => -Math.round(Math.max(0, bank) * 0.05);

/** Нижняя граница яруса: выведена из порога, а не задана вторым полем. */
export const okGate = (gate: number): number => {
  if (gate < 0) return gate;
  return Math.max(50, Math.round((gate * 0.29) / 10) * 10);
};

/** Stored thresholds remain rubles; percent is relative to the configured bank. */
export function gateValue(gate: number, unit: ShowUnit, bank: number, fx: number): number {
  const divisor = new Decimal(unit === 'pct' ? bank : unit === 'usd' ? fx : 1);
  return divisor.gt(0) ? new Decimal(gate).div(divisor).times(unit === 'pct' ? 100 : 1).toNumber() : 0;
}

export function gateText(gate: number, unit: ShowUnit, bank: number, fx: number): string {
  const value = new Decimal(gateValue(gate, unit, bank, fx)).toDecimalPlaces(2).toString();
  return unit === 'pct' ? `${value} %` : unit === 'usd' ? `$${value}` : `${rub(gate)} ₽`;
}

export function stepGate(gate: number, up: boolean, unit: ShowUnit, bank: number, fx: number): number {
  const step = unit === 'pct' ? new Decimal(bank).div(100) : unit === 'usd' ? new Decimal(fx) : new Decimal(GATE_STEP);
  return Decimal.max(gateFloor(bank), new Decimal(gate).plus(step.times(up ? 1 : -1))).toNumber();
}

/** Compare actual ROI in percent mode, even when caps/rounding change the stake. */
export function gateProfit(row: { profit: number; roiPct: number }, unit: ShowUnit, bank: number): number {
  return unit === 'pct' ? new Decimal(row.roiPct).times(bank).div(100).toNumber() : row.profit;
}

export const RU_MIN_ROI = -4;
